/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */

import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { PMSError, ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/connectivity/restrictions`;

const state = {
  salesAllotments: [],
  pricingRestrictions: [],
  minimumStay: [],
  blackoutPeriodsChannel: [],
  pending: false,
  error: null,
  pmsError: null,
  updatePending: false,
  blackoutPeriodsChannelPending: false,
  blackoutPeriodsChannelUpdatePending: false,
  blackoutPeriodRooms: [],

};

const getters = {
  loaded: (state) => !state.pending,
  pending: (state) => state.pending,
  salesAllotments: (state) => (state.salesAllotments),
  pricingRestrictions: (state) => (state.pricingRestrictions),
  minimumStay: (state) => (state.minimumStay),
  blackoutPeriodsChannel: (state) => (state.blackoutPeriodsChannel),
  blackoutPeriodsChannelPending: (state) => (state.blackoutPeriodsChannelPending),
  blackoutPeriodsChannelUpdatePending: (state) => (state.blackoutPeriodsChannelUpdatePending),
  updatePending: (state) => (state.updatePending),
  blackoutPeriodRooms: (state) => (state.blackoutPeriodRooms),
};

const mutations = {
  clearErrors(state) {
    state.error = null;
  },
  beforeLoading(state) {
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.error = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.blackoutPeriodsChannelPending = false;
    state.blackoutPeriodsChannelUpdatePending = false;
    state.error = error;
    state.pmsError = error;
  },
  data(state, data) {
    state.pending = false;
    state.salesAllotments = data;
  },
  modified(state, payload) {
    state.updatePending = false;
    const idx = state.salesAllotments.findIndex(({ roomId }) => roomId === payload.roomTypeCode);
    if (payload.delete) {
      if (idx !== -1) {
        state.salesAllotments.splice(idx, 1);
      }
    }
  },
  pricingRestrictionsData(state, data) {
    state.pending = false;
    state.updatePending = false;
    state.pricingRestrictions = data;
  },
  modifiedPricingRestrictions(state, payload) {
    state.updatePending = false;
    const idx = state.pricingRestrictions.findIndex(({ recordId }) => recordId === payload.recordId);
    if (payload.delete) {
      if (idx !== -1) {
        state.pricingRestrictions.splice(idx, 1);
      }
    }
  },
  minimumStayData(state, data) {
    state.pending = false;
    state.updatePending = false;
    state.minimumStay = data;
  },
  modifiedMinimumStay(state, payload) {
    state.updatePending = false;
    const idx = state.minimumStay.findIndex(({ id }) => id === payload.id);
    if (payload.delete) {
      if (idx !== -1) {
        state.minimumStay.splice(idx, 1);
      }
    }
  },
  blackoutPeriodsChannelBeforeUpdateRequest(state) {
    state.error = null;
    state.blackoutPeriodsChannelUpdatePending = true;
  },
  blackoutPeriodsChannelAfterUpdateRequest(state) {
    state.error = null;
    state.blackoutPeriodsChannelUpdatePending = false;
  },
  blackoutPeriodsChannelBeforeRequest(state) {
    state.error = null;
    state.blackoutPeriodsChannelPending = true;
  },
  blackoutPeriodsChannelAfterRequest(state) {
    state.error = null;
    state.blackoutPeriodsChannelPending = false;
  },
  blackoutPeriodsChannel(state, data) {
    state.blackoutPeriodsChannel = data;
    state.blackoutPeriodsChannelPending = false;
    state.pending = false;
    state.updatePending = false;
  },
  blackoutPeriodRooms(state, data) {
    state.blackoutPeriodRooms = data;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    default:
      break;
  }
  commit('error', err);
  throw err;
}

const actions = {
  async updateSalesAllotmentData({ commit }, payload) {
    commit('beforeUpdate');
    try {
      await ApiService.post(`${endpoint}/sales-allotment`, payload);
      commit('modified', payload);
    } catch (error) {
      commit('error', error.response);
      handleApiError(commit, error);
    }
  },
  async fetchSalesAllotmentData({ commit }, channelId) {
    commit('beforeLoading');
    try {
      const response = await ApiService.get(`${endpoint}/sales-allotment/${channelId}`);
      commit('data', response.data[0]);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async fetchBlackoutPeriodsForChannel({ commit }, channelId) {
    commit('blackoutPeriodsChannelBeforeRequest');
    try {
      const response = await ApiService.get(`${endpoint}/channel-blackout-periods/${channelId}`);
      commit('blackoutPeriodsChannel', response.data[0]);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async updateBlackoutPeriodsForChannel({ commit }, payload) {
    commit('blackoutPeriodsChannelBeforeUpdateRequest');
    try {
      await ApiService.post(`${endpoint}/channel-blackout-periods`, payload);
      commit('blackoutPeriodsChannelAfterUpdateRequest');
    } catch (error) {
      commit('error', error.response);
      handleApiError(commit, error);
    }
  },
  // Minimum stay
  async fetchMinimumStayData({ commit }, channelId) {
    commit('beforeLoading');
    try {
      const response = await ApiService.get(`${endpoint}/minimum-stay/${channelId}`);
      commit('minimumStayData', response.data[0]);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async updateMinimumStayData({ commit }, payload) {
    try {
      commit('beforeUpdate');
      await ApiService.post(`${endpoint}/minimum-stay`, payload);
      commit('modifiedMinimumStay', payload);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  // Pricing
  async fetchPricesData({ commit }, channelId) {
    commit('beforeLoading');
    try {
      const response = await ApiService.get(`${endpoint}/pricing/${channelId}`);
      commit('pricingRestrictionsData', response.data[0]);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async updatePricesData({ commit }, payload) {
    try {
      await ApiService.post(`${endpoint}/pricing`, payload);
      commit('modifiedPricingRestrictions', payload);
    } catch (error) {
      commit('error', error);
      handleApiError(commit, error);
    }
  },
  async fetchBlackoutPeriodRooms({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/rooms`);
      commit('blackoutPeriodRooms', data);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
